import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Dropdown,
  Space,
  Typography,
  Badge,
  Button,
  Drawer,
  List,
  Empty,
  Spin,
} from 'antd';
import {
  DashboardOutlined,
  ClockCircleOutlined,
  CheckSquareOutlined,
  ProjectOutlined,
  TeamOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  CheckOutlined,
  FileExcelOutlined,
  BankOutlined,
  CalculatorOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuthStore } from '@/store/authStore';
import { color, shadow } from '@/theme/tokens';
import { authApi, notificationsApi } from '@/services/api';
import { BrandMark, PersonAvatar, roleLabel } from '@/components';
import { serverTime } from '@/utils/format';
import type { Notification } from '@/types';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const SIDER_WIDTH = 240;
const SIDER_COLLAPSED_WIDTH = 72;

const APP_VERSION = '2.15.0';

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logoutAsync } = useAuthStore();

  const fetchNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const response = await notificationsApi.list({ limit: 10 });
      setNotifications(response.data.items || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // An admin can switch PTO on at any time, so showing it can't wait for the next sign-in
  useEffect(() => {
    authApi.getMe()
      .then(({ data }) => {
        const current = useAuthStore.getState().user;
        if (current && current.pto_eligible !== data.pto_eligible) {
          useAuthStore.getState().updateUser({ ...current, pto_eligible: data.pto_eligible });
        }
      })
      .catch(() => undefined);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.entity_type === 'timesheet') {
      navigate(`/timesheets/${notification.entity_id}`);
    } else if (notification.entity_type === 'project') {
      navigate(`/projects/${notification.entity_id}`);
    } else if (notification.entity_type === 'pto_review') {
      navigate('/approvals?tab=pto');
    } else if (notification.entity_type === 'pto_request') {
      navigate('/pto?tab=mine');
    }
    setNotificationDropdownOpen(false);

    if (!notification.is_read) {
      notificationsApi.markAsRead([notification.id]);
      setNotifications(prev =>
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = serverTime(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const isAdmin = user?.role === 'SUPER_ADMIN';
  const isManager = user?.role === 'PROJECT_MANAGER' || isAdmin;

  const menuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/timesheets',
      icon: <ClockCircleOutlined />,
      label: 'Timesheets',
    },
    ...(user?.pto_eligible || isManager
      ? [
          {
            key: '/pto',
            icon: <CalendarOutlined />,
            label: 'PTO',
          },
        ]
      : []),
    ...(isManager
      ? [
          {
            key: '/approvals',
            icon: <CheckSquareOutlined />,
            label: 'Approvals',
          },
          {
            key: '/projects',
            icon: <ProjectOutlined />,
            label: 'Projects',
          },
          {
            key: '/users',
            icon: <TeamOutlined />,
            label: 'Users',
          },
        ]
      : []),
    ...(isManager
      ? [
          {
            key: '/payroll-calculator',
            icon: <CalculatorOutlined />,
            label: 'Payroll calculator',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/reports',
            icon: <FileExcelOutlined />,
            label: 'Reports',
          },
          {
            key: '/financials',
            icon: <BankOutlined />,
            label: 'Financials',
          },
        ]
      : []),
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign out',
      danger: true,
      onClick: async () => {
        await logoutAsync();
        navigate('/login');
      },
    },
  ];

  const roleText = roleLabel(user?.role);
  const showLabels = !collapsed || isMobile;
  // Timesheet detail/edit pages keep "Timesheets" selected
  const selectedKey = '/' + (location.pathname.split('/')[1] || 'dashboard');

  const sidebarContent = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: color.sidebar }}>
      <div
        style={{
          height: 60,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: showLabels ? 'flex-start' : 'center',
          padding: showLabels ? '0 18px' : 0,
          borderBottom: `1px solid ${color.sidebarBorder}`,
        }}
      >
        <BrandMark compact={!showLabels} />
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        items={menuItems}
        onClick={({ key }) => handleNavigate(key)}
        style={{ background: 'transparent', border: 'none', marginTop: 10, flex: 1 }}
      />

      {showLabels && (
        <div
          style={{
            flex: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            padding: '14px 16px',
            borderTop: `1px solid ${color.sidebarBorder}`,
          }}
        >
          <PersonAvatar firstName={user?.first_name} lastName={user?.last_name} />
          <div style={{ minWidth: 0, lineHeight: 1.3 }}>
            <Text
              style={{
                color: color.white,
                display: 'block',
                fontWeight: 600,
                fontSize: 13.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.first_name} {user?.last_name}
            </Text>
            <Text style={{ color: color.sidebarMuted, fontSize: 12 }}>{roleText}</Text>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          width={260}
          className="tempo-sider"
          styles={{
            body: { padding: 0, background: color.sidebar },
            header: { display: 'none' },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          width={SIDER_WIDTH}
          collapsedWidth={SIDER_COLLAPSED_WIDTH}
          className="tempo-sider"
          style={{
            background: color.sidebar,
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 100,
          }}
        >
          {sidebarContent}
        </Sider>
      )}

      {/* Main Layout */}
      <Layout
        style={{
          marginLeft: isMobile ? 0 : (collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH),
          transition: 'margin-left 0.2s',
        }}
      >
        {/* Header */}
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px 0 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${color.border}`,
            position: 'sticky',
            top: 0,
            zIndex: 99,
          }}
        >
          <Button
            type="text"
            aria-label={isMobile ? 'Open menu' : collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            icon={isMobile ? <MenuUnfoldOutlined /> : (collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />)}
            onClick={() => isMobile ? setMobileDrawerOpen(true) : setCollapsed(!collapsed)}
            style={{ fontSize: 16, width: 40, height: 40, color: color.textSecondary }}
          />

          <Space size={isMobile ? 8 : 14}>
            {/* Notifications */}
            <Dropdown
              open={notificationDropdownOpen}
              onOpenChange={setNotificationDropdownOpen}
              trigger={['click']}
              placement="bottomRight"
              popupRender={() => (
                <div
                  style={{
                    width: isMobile ? 300 : 360,
                    maxHeight: 400,
                    background: color.surface,
                    border: `1px solid ${color.border}`,
                    borderRadius: 8,
                    boxShadow: shadow.popover,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: `1px solid ${color.divider}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text strong style={{ fontSize: 15 }}>Notifications</Text>
                    {unreadCount > 0 && (
                      <Button
                        type="link"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={handleMarkAllRead}
                        style={{ padding: 0, height: 'auto' }}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>

                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {notificationsLoading ? (
                      <div style={{ padding: 40, textAlign: 'center' }}>
                        <Spin size="small" />
                      </div>
                    ) : notifications.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No notifications"
                        style={{ padding: 40 }}
                      />
                    ) : (
                      <List
                        dataSource={notifications}
                        renderItem={(notification) => (
                          <List.Item
                            onClick={() => handleNotificationClick(notification)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              background: notification.is_read ? color.surface : color.surfaceAlt,
                            }}
                          >
                            <List.Item.Meta
                              title={
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {!notification.is_read && (
                                    <span
                                      aria-label="Unread"
                                      style={{ width: 7, height: 7, borderRadius: 1, background: color.coral, flex: 'none' }}
                                    />
                                  )}
                                  <Text strong={!notification.is_read} style={{ fontSize: 13 }}>
                                    {notification.title}
                                  </Text>
                                </span>
                              }
                              description={
                                <div>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {notification.message}
                                  </Text>
                                  <br />
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {formatTimeAgo(notification.created_at)}
                                  </Text>
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )}
                  </div>
                </div>
              )}
            >
              <Badge count={unreadCount} size="small">
                <Button
                  type="text"
                  aria-label="Notifications"
                  icon={<BellOutlined style={{ fontSize: 18 }} />}
                  style={{ width: 40, height: 40, color: color.textSecondary }}
                />
              </Badge>
            </Dropdown>

            {/* User Dropdown */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Space style={{ cursor: 'pointer', padding: '4px 6px' }}>
                <PersonAvatar firstName={user?.first_name} lastName={user?.last_name} />
                {!isMobile && (
                  <div style={{ lineHeight: 1.25 }}>
                    <Text strong style={{ display: 'block', fontSize: 13.5 }}>
                      {user?.first_name} {user?.last_name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {roleText}
                    </Text>
                  </div>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* Content */}
        <Content
          style={{
            margin: isMobile ? 12 : 24,
            minHeight: 'calc(100vh - 160px)',
          }}
        >
          <Outlet />
        </Content>

        {/* Footer */}
        <div
          style={{
            textAlign: 'center',
            padding: '14px 24px',
            borderTop: `1px solid ${color.border}`,
          }}
        >
          <Text type="secondary" style={{ fontSize: 11.5 }}>
            <Text strong style={{ fontSize: 11.5 }}>Tempo</Text>
            {' '}v{APP_VERSION} · Quire Systems © {new Date().getFullYear()}
          </Text>
        </div>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
